import { useState } from 'react';
import { TabbedInterface, EmptyState } from '@ai-tutor/ui';
import {
  FolderOpen,
  BookOpen,
  BookMarked,
  HelpCircle,
  LinkIcon,
} from 'lucide-react';
import { mockGlossaryTerms, type Source } from '@/data/fixtures';
import { SourceViewer } from './SourceViewer';
import { GlossaryPanel } from './GlossaryPanel';
import { ExtrasManager } from './ExtrasManager';
import { ClassifiedList } from '../common/ClassifiedList';
import { requestChatPrefill } from '@/lib/chat-prefill';
import { ComingSoon } from '@/components/core/common/feedback/ComingSoon';
import { PanelBoundary } from '@/components/core/common/boundaries/PanelBoundary';
import { useLessonTabParam } from '@/hooks/lesson/useLessonTabParam';
import { useSourcesList } from '@/hooks/lesson/useSources';
import { StepGate } from '../common/StepGate';
import { ActivityTrackedTab } from '../common/ActivityTrackedTab';
import { GlossarySkeleton, DoubtsSkeleton } from '../skeletons/PanelSkeletons';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  useGlossary,
  useGenerateContent,
  useRetryContent,
} from '@/hooks/lesson/useOnDemandContent';
import { useClassifiedChat } from '@/hooks/chat/useClassifiedChat';

import type { StepView } from '@/types/pipeline';
import type { Visual } from '@/types/visual';

interface ReferenceViewerProps {
  lessonId?: string;
  lessonStyle?: string;
  selectedSource?: Source | null;
  onProgressUpdate?: (sourcesViewed: any, glossaryViewed: any) => void;
  /** Per-step view map; the Glossary `<StepGate>` reads `steps.glossary`. */
  steps?: Record<string, StepView>;
  /** True once the lesson's `overall_status` is terminal - off-flow gating. */
  lessonDone?: boolean;
  /**
   * True once the page has both lesson metadata + pipeline-status
   * snapshot loaded. Threaded into the Glossary `<StepGate>` so it
   * shows a neutral "Loading Glossary" skeleton during the initial-
   * load gap instead of flashing "Waiting" or a Generate CTA.
   */
  lessonReady?: boolean;
}

/** Sub-tab ids; `references` is the Extras tab. */
const REFERENCE_VIEWS = [
  'sources',
  'glossary',
  'doubts',
  'references',
] as const;

export function ReferenceViewer({
  lessonId,
  lessonStyle: __,
  selectedSource,
  onProgressUpdate,
  steps,
  lessonDone,
  lessonReady,
}: ReferenceViewerProps) {
  const { isMock, isApi, isEmpty } = useDataMode();
  // `?view=` — see `useLessonTabParam`.
  const [activeTab, setActiveTab] = useLessonTabParam(
    'view',
    REFERENCE_VIEWS,
    'sources'
  );
  const [searchTerm, setSearchTerm] = useState('');

  // API hooks
  const glossaryApi = useGlossary(isApi ? lessonId : undefined);
  // The left panel's own list query (same cache key, no extra request) — only
  // to tell the Source Viewer whether there is anything to pick.
  const sourcesList = useSourcesList(isApi ? lessonId : undefined);
  const hasSources =
    isApi && !sourcesList.isLoading && !sourcesList.error
      ? sourcesList.sources.length > 0
      : undefined;
  const generateContent = useGenerateContent(lessonId || '');
  const retryContent = useRetryContent(lessonId || '');
  // Classified chat (doubts + deep-dives) - drives the Doubts tab here
  // and the Extras' Suggested Deep-Dives section. One shared cache key.
  const classifiedChat = useClassifiedChat(isApi ? lessonId : undefined);

  // Determine data source for glossary
  const glossaryTerms = isApi
    ? glossaryApi.terms.map((t, i) => ({
        id: `gt-${i}`,
        term: t.term,
        definition: t.definition,
        category: t.category,
        examples: t.example ? [t.example] : undefined,
        // The term's concept picture (on_demand.py); the glossary response
        // is untyped, so read it as optional.
        visual: (t as { visual?: Visual | null }).visual ?? null,
      }))
    : isMock
      ? mockGlossaryTerms
      : [];

  const sourcesContent = (
    <SourceViewer
      selectedSource={selectedSource || null}
      hasSources={hasSources}
      onProgressUpdate={sourceId => onProgressUpdate?.(sourceId, false)}
    />
  );

  const glossaryUI = (
    <GlossaryPanel
      terms={glossaryTerms}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      // Step is `completed` so StepGate renders us, but the /glossary content
      // query can still be in flight — see the note in `GlossaryPanel`.
      isLoading={isApi && glossaryApi.isLoading}
    />
  );

  // Glossary Content - route by data mode
  const glossaryContent = isEmpty ? (
    <ComingSoon
      icon={BookMarked}
      title='Interactive Glossary'
      description='Search and explore key terms and definitions from your lesson.'
    />
  ) : isApi ? (
    <StepGate
      step='glossary'
      lessonId={lessonId}
      steps={steps}
      lessonDone={lessonDone}
      lessonReady={lessonReady}
      skeleton={<GlossarySkeleton />}
      isGenerating={
        generateContent.pendingType === 'glossary' ||
        retryContent.pendingType === 'glossary'
      }
      generate={{
        icon: BookMarked,
        label: 'glossary',
        description:
          'Generate a glossary of key terms and definitions from this lesson.',
        onGenerate: () => generateContent.mutate('glossary'),
      }}
      onRetry={() => retryContent.mutate('glossary')}
    >
      {glossaryUI}
    </StepGate>
  ) : (
    glossaryUI
  );

  // Extras content - manually-curated attachments. The UI lives in
  // ExtrasManager and only makes sense in API mode (the mock + empty
  // modes still show their stub views since there's no lessonId).
  const referencesContent = isEmpty ? (
    <ComingSoon
      icon={LinkIcon}
      title='Learning Resources'
      description='Attach lessons from your library or external links to keep your references in one place.'
    />
  ) : isApi && lessonId ? (
    <PanelBoundary key='extras' name='Extras'>
      <ExtrasManager lessonId={lessonId} />
    </PanelBoundary>
  ) : (
    <EmptyState
      icon={LinkIcon}
      title='Extras'
      description='Open this lesson to add attachments.'
    />
  );

  // Doubts tab - read-only Q&A list of chat turns the classifier
  // labeled as "doubt". Lives here in the Appendix because doubts are
  // reference material for the lesson; deep-dives go to Extras as
  // promotion candidates.
  const doubtsContent =
    isApi && lessonId ? (
      <div className='h-full overflow-y-auto'>
        {classifiedChat.isLoading ? (
          // Query still in flight - show the panel loading screen (matches
          // Extras/Glossary) instead of flashing the "No doubts yet" empty
          // state before the classified turns load.
          <DoubtsSkeleton mode='fetching' />
        ) : (
          <ClassifiedList
            entries={classifiedChat.doubts}
            icon={HelpCircle}
            emptyTitle='No doubts yet'
            emptyDescription='Questions you ask in chat that look like a doubt show up here.'
            countLabel={n => `${n} doubt${n === 1 ? '' : 's'} from your chats`}
            // Picks the thread back up in the chat - on a phone, the
            // prefill opens the chat sheet (`LessonCompactLayout`).
            onFollowUp={entry =>
              requestChatPrefill(`Following up on "${entry.question}": `)
            }
          />
        )}
      </div>
    ) : (
      <EmptyState
        icon={HelpCircle}
        title='Doubts'
        description='Open this lesson to see classified doubts from your chats.'
      />
    );

  // Each sub-tab wrapped in its own PanelBoundary so a crash in one
  // (e.g. Glossary) doesn't take down the others (e.g. Doubts still
  // navigable). Extras already has its own inner `<PanelBoundary
  // name='Extras'>` inside `ExtrasManager`'s render path - wrapping it
  // here again is harmless (the inner one catches first; this outer
  // wrapper is just defense-in-depth for crashes that happen before
  // the inner boundary mounts).
  const tabs = [
    {
      id: 'sources',
      label: 'Source Viewer',
      icon: FolderOpen,
      content: (
        <PanelBoundary key='sources' name='Sources'>
          <ActivityTrackedTab contentType='reference' lessonId={lessonId}>
            {sourcesContent}
          </ActivityTrackedTab>
        </PanelBoundary>
      ),
    },
    {
      id: 'glossary',
      label: 'Glossary',
      icon: BookOpen,
      count: glossaryTerms.length || undefined,
      content: (
        <PanelBoundary key='glossary' name='Glossary'>
          <ActivityTrackedTab contentType='reference' lessonId={lessonId}>
            {glossaryContent}
          </ActivityTrackedTab>
        </PanelBoundary>
      ),
    },
    {
      id: 'doubts',
      label: 'Doubts',
      icon: HelpCircle,
      count: classifiedChat.totalDoubts || undefined,
      content: (
        <PanelBoundary key='doubts' name='Doubts'>
          <ActivityTrackedTab contentType='reference' lessonId={lessonId}>
            {doubtsContent}
          </ActivityTrackedTab>
        </PanelBoundary>
      ),
    },
    {
      id: 'references',
      label: 'Extras',
      icon: LinkIcon,
      // Extras' content (`referencesContent`) already wraps `ExtrasManager`
      // in `<PanelBoundary name='Extras'>` so we don't double-wrap here.
      // ExtrasManager itself calls `useActivityTracking` for the
      // 'reference' content type, so no wrapper is needed at this level.
      content: referencesContent,
    },
  ];

  return (
    <div className='flex h-full flex-col'>
      <TabbedInterface
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={tab =>
          setActiveTab(tab as (typeof REFERENCE_VIEWS)[number])
        }
        variant='pills'
        className='flex-1'
        showCounts={false}
      />
    </div>
  );
}
